import React, { useMemo } from "react";
import { useWizard } from "@/context/WizardContext";
import { TextInput, SelectInput, SectionHeading, FieldLabel } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";
import { ChevronDown } from "lucide-react";

const YN = ["Yes", "No"];
const YNU = ["Yes", "No", "Unknown"];
const INDEP_LEVELS = ["Independent", "Some Help", "Dependent"];

// All <SelectInput options={...}> arrays pinned at module scope so each
// render shares the same array reference (avoids needless child re-renders).
const HEALTH_STATUS = ["Excellent", "Good", "Guarded", "Poor", "Terminal"];
const ABLE_TO_VOICE = ["Yes", "Partially", "No"];
const SHOWER_INDEP = ["Independent", "Needs assistance", "Totally dependent"];
const NA_OPT = [{ value: "", label: "—" }, { value: "NA", label: "N/A" }];
const MOUTH_STATES = ["Normal", "Abnormal oral mucosa", "Gum problem", "Other"];
const DENTURES = ["None", "Upper", "Lower", "Full (upper + lower)"];
const DEGREES_LMH = ["Light", "Moderate", "Heavy"];
const ALCOHOL_DEGREES = ["Occasional", "Moderate", "Heavy"];
const PARTIAL_FULL = ["None", "Partial", "Full"];
const SIG_RELATIONSHIP = ["Self", "Authorized Representative"];

// ---------------------------------------------------------------------------
// Shared layout helpers
// ---------------------------------------------------------------------------
function SectionWrap({ eyebrow, title, description, children, testid }) {
  return (
    <section className="card-surface fade-up" data-testid={testid}>
      <SectionHeading eyebrow={eyebrow} title={title} description={description} />
      {children}
    </section>
  );
}
function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
}

// One-tap "Denies All / None" mega button — sets every field in `fields` to "No".
function DeniesAllButton({ onClick, label = "Client denies all · mark all No", testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="h-9 px-4 rounded-md border border-[#B7CADA] bg-[#E7F1FB] text-[#0A3D79] text-[12.5px] font-medium hover:bg-[#D0E4F5] transition"
    >
      {label}
    </button>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Preset Profiles — one-tap starter templates
// ---------------------------------------------------------------------------
const PRESET_PROFILES = {
  typical_elderly: {
    label: "Typical elderly homebound",
    blurb: "Denies abuse/neglect, independent finances, no smoking/alcohol, ≥65 on MAHC.",
    apply: (data) => ({
      assessment: {
        ...data.assessment,
        safety_screen: {
          suspected_neglect: "No", suspected_physical_abuse: "No",
          suspected_financial_abuse: "No", inadequate_cooking_shopping: "No",
          insects_rodents: "No", left_unattended_when_needs_supervision: "No",
          msw_referral_made: "No", community_resources_needed: "No", notes: "",
        },
        financial: {
          finances_handling: "Independent", inappropriate_income_use: "No",
          inadequate_for_necessities: "No", uncovered_medical_expenses: "No", notes: "",
        },
        caregiver_capability: {
          family_supportive: "Yes", caregiver_able_willing: "Yes",
          caregiver_follows_instructions: "Yes", caregiver_assists_adls: "Yes",
          caregiver_safe: "Yes", notes: "",
        },
        prognosis: { ...data.assessment?.prognosis, prognosis: "Good", family_understands_dx: "Yes" },
      },
    }),
  },
  post_hospital: {
    label: "Post-hospital discharge",
    blurb: "Elevates fall risk; flags recent hospitalization + PT/OT follow-up.",
    apply: (data) => ({
      assessment: {
        ...data.assessment,
        safety_screen: {
          suspected_neglect: "No", suspected_physical_abuse: "No",
          suspected_financial_abuse: "No", inadequate_cooking_shopping: "No",
          insects_rodents: "No", left_unattended_when_needs_supervision: "No",
          msw_referral_made: "No", community_resources_needed: "Yes", notes: "",
        },
        prognosis: { ...data.assessment?.prognosis, prognosis: "Guarded", family_understands_dx: "Partially" },
        mahc10: {
          ...data.assessment?.mahc10,
          prior_falls_3mo: "Yes", mobility_impairment: "Yes", pain_affecting_function: "Yes",
        },
      },
    }),
  },
  cognitive_decline: {
    label: "Cognitive decline baseline",
    blurb: "Sets cognitive/behavioral markers for MAHC-10 and mood sections.",
    apply: (data) => ({
      assessment: {
        ...data.assessment,
        safety_screen: {
          suspected_neglect: "No", suspected_physical_abuse: "No",
          suspected_financial_abuse: "No", inadequate_cooking_shopping: "Yes",
          insects_rodents: "No", left_unattended_when_needs_supervision: "No",
          msw_referral_made: "No", community_resources_needed: "Yes", notes: "",
        },
        mahc10: {
          ...data.assessment?.mahc10,
          cognitive_impairment: "Yes", mobility_impairment: "Yes",
        },
        prognosis: { ...data.assessment?.prognosis, prognosis: "Guarded", family_understands_dx: "Partially" },
      },
    }),
  },
};

export function PresetProfileBar() {
  const { data, setData } = useWizard();
  return (
    <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-[#DBB90A] bg-[#FFFDEC]" data-testid="nsa-preset-bar">
      <span className="text-[11.5px] uppercase tracking-[0.14em] text-[#8A6B00] font-semibold self-center">
        Preset profiles →
      </span>
      {Object.entries(PRESET_PROFILES).map(([id, p]) => (
        <button
          key={id}
          type="button"
          title={p.blurb}
          onClick={() => setData((prev) => ({ ...prev, ...p.apply(prev) }))}
          data-testid={`nsa-preset-${id}`}
          className="h-8 px-3 rounded-md border border-[#DBB90A] bg-white text-[#8A6B00] text-[12.5px] font-medium hover:bg-[#FFF3B3] transition"
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Immunizations
// ---------------------------------------------------------------------------
const IMMUNIZATION_ROWS = [
  { key: "tetanus",   label: "Tetanus (Td/Tdap)" },
  { key: "pneumonia", label: "Pneumonia (PCV/PPSV)" },
  { key: "hepatitis", label: "Hepatitis (A or B)" },
  { key: "influenza", label: "Influenza (annual)" },
  { key: "h1n1",      label: "H1N1 (novel)" },
];

export function ImmunizationsSection() {
  const { data, update } = useWizard();
  const a = data.assessment || {};
  const imm = a.immunizations || {};
  const markAllUnknown = () => {
    const next = {};
    IMMUNIZATION_ROWS.forEach((r) => (next[r.key] = { given: "Unknown", date: "" }));
    update("assessment", { immunizations: { ...imm, ...next } });
  };
  return (
    <SectionWrap eyebrow="Step 7 · Immunizations" title="Immunization Status"
      testid="nsa-section-immunizations"
      description="Record the most-recent dose for each.">
      <div className="mb-3">
        <DeniesAllButton onClick={markAllUnknown} label="Mark all Unknown" testid="nsa-imm-all-unknown" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-[#D6E3EF]">
              <th className="text-left py-2 font-medium text-[#1E2E4A]">Immunization</th>
              <th className="text-left py-2 font-medium text-[#1E2E4A] w-[180px]">Given?</th>
              <th className="text-left py-2 font-medium text-[#1E2E4A] w-[200px]">Date</th>
            </tr>
          </thead>
          <tbody>
            {IMMUNIZATION_ROWS.map((r) => {
              const row = imm[r.key] || {};
              return (
                <tr key={r.key} className="border-b border-[#EEF5FC]" data-testid={`nsa-imm-row-${r.key}`}>
                  <td className="py-2 text-[#0B1E3A]">{r.label}</td>
                  <td className="py-2">
                    <SelectInput compact hideLabel value={row.given}
                      onChange={(v) => update("assessment", { immunizations: { ...imm, [r.key]: { ...row, given: v } } })}
                      options={YNU} testid={`nsa-imm-${r.key}-given`} />
                  </td>
                  <td className="py-2">
                    <TextInput compact hideLabel type="date" value={row.date}
                      onChange={(v) => update("assessment", { immunizations: { ...imm, [r.key]: { ...row, date: v } } })}
                      testid={`nsa-imm-${r.key}-date`} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Prognosis + ER triggers + Family understanding
// ---------------------------------------------------------------------------
export function PrognosisSection() {
  const { data, update } = useWizard();
  const pr = data.assessment?.prognosis || {};
  return (
    <SectionWrap eyebrow="Step 7 · Prognosis" title="Prognosis & ER History"
      testid="nsa-section-prognosis">
      <Row>
        <SelectInput label="Prognosis" value={pr.prognosis}
          onChange={(v) => update("assessment", { prognosis: { ...pr, prognosis: v } })}
          options={HEALTH_STATUS}
          testid="nsa-prognosis-level" />
        <SelectInput label="Patient/family understands diagnoses?"
          value={pr.family_understands_dx}
          onChange={(v) => update("assessment", { prognosis: { ...pr, family_understands_dx: v } })}
          options={ABLE_TO_VOICE}
          testid="nsa-prognosis-understand" />
        <TextInput label="Recent hospitalizations / ER visits (count · date)"
          value={pr.recent_er_visits}
          onChange={(v) => update("assessment", { prognosis: { ...pr, recent_er_visits: v } })}
          testid="nsa-prognosis-er-visits" />
      </Row>
      <div className="mt-3">
        <NarrativeField label="What makes the patient want/need to go to the hospital?"
          testid="nsa-prognosis-er-triggers"
          fieldKey="assessment.prognosis.er_triggers"
          fieldPurpose="Specific symptoms, triggers, or behavioral changes that warrant ER escalation."
          value={pr.er_triggers}
          onChange={(v) => update("assessment", { prognosis: { ...pr, er_triggers: v } })} />
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Caregiver capability (distinct from Zarit burden)
// ---------------------------------------------------------------------------
export function CaregiverCapabilitySection() {
  const { data, update } = useWizard();
  const cc = data.assessment?.caregiver_capability || {};
  const setAllYes = () => update("assessment", {
    caregiver_capability: {
      family_supportive: "Yes", caregiver_able_willing: "Yes",
      caregiver_follows_instructions: "Yes", caregiver_assists_adls: "Yes",
      caregiver_safe: "Yes", notes: cc.notes || "",
    },
  });
  return (
    <SectionWrap eyebrow="Step 7 · Caregiver Capability" title="Caregiver Capability & Family Support"
      testid="nsa-section-caregiver-cap"
      description="Separate from Zarit burden — these are go/no-go clinical flags.">
      <div className="mb-3">
        <DeniesAllButton onClick={setAllYes} label="All supportive & capable · mark all Yes" testid="nsa-cg-cap-all-yes" />
      </div>
      <Row>
        <SelectInput label="Family supportive?" value={cc.family_supportive}
          onChange={(v) => update("assessment", { caregiver_capability: { ...cc, family_supportive: v } })}
          options={YNU} testid="nsa-cg-cap-family" />
        <SelectInput label="Caregiver able & willing?" value={cc.caregiver_able_willing}
          onChange={(v) => update("assessment", { caregiver_capability: { ...cc, caregiver_able_willing: v } })}
          options={YN} testid="nsa-cg-cap-able" />
        <SelectInput label="Follows instructions?" value={cc.caregiver_follows_instructions}
          onChange={(v) => update("assessment", { caregiver_capability: { ...cc, caregiver_follows_instructions: v } })}
          options={YN} testid="nsa-cg-cap-instructions" />
        <SelectInput label="Assists with ADLs?" value={cc.caregiver_assists_adls}
          onChange={(v) => update("assessment", { caregiver_capability: { ...cc, caregiver_assists_adls: v } })}
          options={YN} testid="nsa-cg-cap-adls" />
        <SelectInput label="Able to safely care for patient?" value={cc.caregiver_safe}
          onChange={(v) => update("assessment", { caregiver_capability: { ...cc, caregiver_safe: v } })}
          options={YN} testid="nsa-cg-cap-safe" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Oxygen safety (conditional)
// ---------------------------------------------------------------------------
export function OxygenSafetySection() {
  const { data, update } = useWizard();
  const a = data.assessment || {};
  // Only render when oxygen is in the home
  if (a.emergency?.oxygen_in_home !== "Yes") return null;
  const ox = a.oxygen_safety || {};
  const allSafe = () => update("assessment", {
    oxygen_safety: { sign_posted: "Yes", eight_feet_from_flame: "Yes", backup_tank: "Yes" },
  });
  return (
    <SectionWrap eyebrow="Step 7 · Oxygen Safety" title="Oxygen Safety Checks"
      testid="nsa-section-oxygen"
      description="Required only when O₂ is in the home (auto-shown).">
      <div className="mb-3">
        <DeniesAllButton onClick={allSafe} label="All safe · mark all Yes" testid="nsa-ox-all-yes" />
      </div>
      <Row>
        <SelectInput label="'Oxygen in Use' sign posted" value={ox.sign_posted}
          onChange={(v) => update("assessment", { oxygen_safety: { ...ox, sign_posted: v } })}
          options={YN} testid="nsa-ox-sign" />
        <SelectInput label="Kept > 8 ft from open flames" value={ox.eight_feet_from_flame}
          onChange={(v) => update("assessment", { oxygen_safety: { ...ox, eight_feet_from_flame: v } })}
          options={YN} testid="nsa-ox-flame" />
        <SelectInput label="Backup tank available" value={ox.backup_tank}
          onChange={(v) => update("assessment", { oxygen_safety: { ...ox, backup_tank: v } })}
          options={YN} testid="nsa-ox-backup" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Safety & Abuse screening
// ---------------------------------------------------------------------------
const SAFETY_ITEMS = [
  { key: "suspected_neglect", label: "Suspected neglect" },
  { key: "suspected_physical_abuse", label: "Suspected physical abuse" },
  { key: "suspected_financial_abuse", label: "Suspected financial abuse" },
  { key: "inadequate_cooking_shopping", label: "Inadequate method to cook or shop" },
  { key: "insects_rodents", label: "Insects / rodents present" },
  { key: "left_unattended_when_needs_supervision", label: "Left unattended when needs constant supervision" },
  { key: "msw_referral_made", label: "MSW referral made" },
  { key: "community_resources_needed", label: "Community-resource information needed" },
];

export function SafetyAbuseSection() {
  const { data, update } = useWizard();
  const s = data.assessment?.safety_screen || {};
  const markAllNo = () => {
    const next = {};
    SAFETY_ITEMS.forEach((i) => { next[i.key] = "No"; });
    update("assessment", { safety_screen: { ...s, ...next } });
  };
  return (
    <SectionWrap eyebrow="Step 7 · Safety & Abuse" title="Safety, Neglect & Abuse Screening"
      testid="nsa-section-safety"
      description="Every 'Yes' here should pair with an MSW referral or agency protocol trigger.">
      <div className="mb-3">
        <DeniesAllButton onClick={markAllNo} testid="nsa-safety-all-no" />
      </div>
      <Row>
        {SAFETY_ITEMS.map((i) => (
          <SelectInput key={i.key} label={i.label} value={s[i.key]}
            onChange={(v) => update("assessment", { safety_screen: { ...s, [i.key]: v } })}
            options={YN} testid={`nsa-safety-${i.key.replace(/_/g, "-")}`} />
        ))}
      </Row>
      <div className="mt-3">
        <NarrativeField label="Safety notes" testid="nsa-safety-notes"
          fieldKey="assessment.safety_screen.notes"
          fieldPurpose="Details on any Yes flags, escalations, or MSW/law-enforcement referrals."
          value={s.notes} onChange={(v) => update("assessment", { safety_screen: { ...s, notes: v } })} />
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Financial
// ---------------------------------------------------------------------------
export function FinancialSection() {
  const { data, update } = useWizard();
  const f = data.assessment?.financial || {};
  return (
    <SectionWrap eyebrow="Step 7 · Financial" title="Financial Status"
      testid="nsa-section-financial">
      <Row>
        <SelectInput label="Handles personal finances" value={f.finances_handling}
          onChange={(v) => update("assessment", { financial: { ...f, finances_handling: v } })}
          options={SHOWER_INDEP}
          testid="nsa-fin-handling" />
        <SelectInput label="Inappropriate use of income" value={f.inappropriate_income_use}
          onChange={(v) => update("assessment", { financial: { ...f, inappropriate_income_use: v } })}
          options={YN} testid="nsa-fin-inappropriate" />
        <SelectInput label="Inadequate for necessities" value={f.inadequate_for_necessities}
          onChange={(v) => update("assessment", { financial: { ...f, inadequate_for_necessities: v } })}
          options={YN} testid="nsa-fin-inadequate" />
        <SelectInput label="Uncovered medical expenses" value={f.uncovered_medical_expenses}
          onChange={(v) => update("assessment", { financial: { ...f, uncovered_medical_expenses: v } })}
          options={YN} testid="nsa-fin-uncovered" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Teaching materials checklist
// ---------------------------------------------------------------------------
const TEACHING_ITEMS = [
  { key: "rights_responsibilities",  label: "Rights & responsibilities" },
  { key: "advance_directives",       label: "Advance directives" },
  { key: "dnr_hospice",              label: "DNR / hospice info" },
  { key: "hipaa",                    label: "HIPAA notice of privacy" },
  { key: "oasis_privacy",            label: "OASIS privacy notice" },
  { key: "agency_contact_info",      label: "Agency contact info" },
  { key: "when_to_contact_nurse_md", label: "When to contact nurse / MD" },
  { key: "infection_precautions",    label: "Infection precautions" },
  { key: "home_safety",              label: "Home safety" },
  { key: "sharps_disposal",          label: "Sharps disposal" },
  { key: "medication_safety",        label: "Medication safety" },
  { key: "emergency_preparedness",   label: "Emergency preparedness" },
];

export function TeachingSection() {
  const { data, update } = useWizard();
  const t = data.assessment?.teaching || {};
  const allProvided = () => {
    const next = {};
    TEACHING_ITEMS.forEach((i) => { next[i.key] = true; });
    update("assessment", { teaching: { ...t, ...next } });
  };
  return (
    <SectionWrap eyebrow="Step 7 · Teaching" title="Teaching Materials Provided"
      testid="nsa-section-teaching"
      description="Check each item the patient / family received.">
      <div className="mb-3">
        <DeniesAllButton onClick={allProvided} label="All provided · check all" testid="nsa-teach-all" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        {TEACHING_ITEMS.map((i) => (
          <label key={i.key}
            className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[13px] cursor-pointer transition ${
              t[i.key]
                ? "bg-[#E7F1FB] border-[#1FA0E6] text-[#0B1E3A]"
                : "bg-white border-[#D6E3EF] text-[#3A5275] hover:border-[#B7CADA]"
            }`}>
            <input type="checkbox" checked={!!t[i.key]}
              onChange={(e) => update("assessment", { teaching: { ...t, [i.key]: e.target.checked } })}
              data-testid={`nsa-teach-${i.key.replace(/_/g, "-")}`}
              className="w-4 h-4 accent-[#0A3D79]" />
            <span>{i.label}</span>
          </label>
        ))}
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: MAHC-10 Fall Risk
// ---------------------------------------------------------------------------
export const MAHC10_ITEMS = [
  { key: "age_65_plus",              label: "Age 65 or older" },
  { key: "diagnoses_three_plus",     label: "≥ 3 co-existing diagnoses" },
  { key: "prior_falls_3mo",          label: "Prior history of falls within 3 months" },
  { key: "incontinence",             label: "Incontinence" },
  { key: "visual_impairment",        label: "Visual impairment" },
  { key: "polypharmacy",             label: "Polypharmacy (≥ 4 prescriptions)" },
  { key: "pain_affecting_function",  label: "Pain affecting level of function" },
  { key: "cognitive_impairment",     label: "Cognitive impairment" },
  { key: "mobility_impairment",      label: "Impaired functional mobility" },
  { key: "environmental_hazards",    label: "Environmental hazards" },
];

function ageYears(dob) {
  if (!dob) return null;
  const d = new Date(dob);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let y = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) y--;
  return y;
}

// Derive MAHC-10 auto-values from the rest of the wizard data.
export function deriveMahc10(data) {
  const age = ageYears(data?.client?.dob);
  const dxLen = (data?.diagnoses || []).filter((d) => d.icd10 || d.description).length;
  const medsLen = (data?.medications || []).filter((m) => m.name).length;
  const vision = (data?.communication?.vision || "").toLowerCase();
  const bladder = (data?.assessment?.continence?.bladder_pattern || "").toLowerCase();
  const bowel = (data?.assessment?.continence?.bowel_pattern || "").toLowerCase();
  const painInt = (data?.assessment?.pain?.interferes_adls || "");
  const mobilityLvl = (data?.adl?.mobility?.pcst_level || "").toUpperCase();
  // Match both adjective ("incontinent") and noun ("incontinence") forms
  const isIncontinent = (s) => /\binconti/.test(s);
  return {
    age_65_plus: age != null ? (age >= 65 ? "Yes" : "No") : "",
    diagnoses_three_plus: dxLen >= 3 ? "Yes" : dxLen > 0 ? "No" : "",
    polypharmacy: medsLen >= 4 ? "Yes" : medsLen > 0 ? "No" : "",
    visual_impairment: vision && vision !== "normal" && vision !== "" ? "Yes" : vision === "normal" ? "No" : "",
    incontinence: (isIncontinent(bladder) || isIncontinent(bowel)) ? "Yes"
      : (bladder === "continent" && bowel === "regular") ? "No" : "",
    pain_affecting_function: painInt || "",
    mobility_impairment: ["C", "D"].includes(mobilityLvl) ? "Yes" : mobilityLvl === "A" ? "No" : "",
  };
}

export function MAHC10Section() {
  const { data, update } = useWizard();
  const m = data.assessment?.mahc10 || {};
  const derived = useMemo(() => deriveMahc10(data), [data]);

  const applyDerived = () => update("assessment", {
    mahc10: { ...m, ...Object.fromEntries(Object.entries(derived).filter(([, v]) => v)) },
  });
  const total = MAHC10_ITEMS.reduce((sum, i) => sum + (m[i.key] === "Yes" ? 1 : 0), 0);
  const risk = total >= 4 ? "High Risk (≥4)" : "Not at High Risk";

  return (
    <SectionWrap eyebrow="Step 7 · Fall Risk (MAHC-10)" title="MAHC-10 Fall Risk Assessment"
      testid="nsa-section-mahc10"
      description="Home-care-validated tool (Missouri Alliance for Home Care). Score ≥ 4 = High Fall Risk. 7 of 10 items can auto-fill from earlier data.">
      <div className="mb-3 flex items-center gap-3">
        <button type="button" onClick={applyDerived}
          className="h-9 px-4 rounded-md border border-[#b6c9dc] bg-[#eaf1f7] text-[#0f3b5f] text-[12.5px] font-medium hover:bg-[#dce9f3]"
          data-testid="nsa-mahc-auto">
          Auto-fill {Object.values(derived).filter(Boolean).length} from entered data
        </button>
        <span className="text-[12px] text-[#3A5275]">(You can override any auto-filled answer.)</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="border-b border-[#D6E3EF]">
              <th className="text-left py-2 font-medium text-[#1E2E4A]">Risk factor</th>
              <th className="text-left py-2 font-medium text-[#1E2E4A] w-[180px]">Answer</th>
              <th className="text-left py-2 font-medium text-[#1E2E4A] w-[120px]">Suggested</th>
            </tr>
          </thead>
          <tbody>
            {MAHC10_ITEMS.map((i) => (
              <tr key={i.key} className="border-b border-[#EEF5FC]">
                <td className="py-2 text-[#0B1E3A]">{i.label}</td>
                <td className="py-2">
                  <SelectInput compact hideLabel value={m[i.key]}
                    onChange={(v) => update("assessment", { mahc10: { ...m, [i.key]: v } })}
                    options={YN} testid={`nsa-mahc-${i.key.replace(/_/g, "-")}`} />
                </td>
                <td className="py-2 text-[12px] text-[#3A5275] font-mono">
                  {derived[i.key] || "—"}
                </td>
              </tr>
            ))}
            <tr className="bg-[#EEF5FC] font-semibold">
              <td className="py-2">Total · Risk</td>
              <td className="py-2" data-testid="nsa-mahc-total">{total} / 10</td>
              <td className="py-2" data-testid="nsa-mahc-risk">{risk}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: GG-style functional scores
// ---------------------------------------------------------------------------
export const GG_ITEMS = [
  { key: "grooming",          label: "Grooming",            max: 3 },
  { key: "dress_upper",       label: "Dress upper body",    max: 3 },
  { key: "dress_lower",       label: "Dress lower body",    max: 3 },
  { key: "bathing",           label: "Bathing",             max: 6 },
  { key: "toilet_transfer",   label: "Toilet transfer",     max: 4 },
  { key: "toileting_hygiene", label: "Toileting hygiene",   max: 3 },
  { key: "transfer",          label: "Transfer (bed/chair)",max: 5 },
  { key: "ambulation",        label: "Ambulation / locomotion", max: 6 },
  { key: "eating",            label: "Eating",              max: 5 },
  { key: "meal_prep_light",   label: "Light meal prep",     max: 2 },
  { key: "telephone",         label: "Telephone use",       max: 5 },
];

// Roughly map PCST letter codes → GG mid-range suggestion (higher = more independent in GG).
function suggestGGFromPCST(level, max) {
  const map = { A: 1.0, B: 0.85, C: 0.65, D: 0.4, E: 0.2, F: 0.1, G: 0.1, H: 0.1 };
  const v = (level || "").toUpperCase();
  if (!map[v]) return "";
  return String(Math.round(max * map[v]));
}

export function GGScoresSection() {
  const { data, update } = useWizard();
  const gg = data.assessment?.gg_functional || {};

  const autoSuggest = () => {
    const next = {};
    const adl = data.adl || {};
    const pick = (adlKey, ggKey, max) => {
      const s = suggestGGFromPCST(adl[adlKey]?.pcst_level, max);
      if (s) next[ggKey] = s;
    };
    pick("bathing", "bathing", 6);
    pick("dressing", "dress_upper", 3);
    pick("dressing", "dress_lower", 3);
    pick("grooming", "grooming", 3);
    pick("eating", "eating", 5);
    pick("mobility", "ambulation", 6);
    pick("toileting", "toilet_transfer", 4);
    pick("toileting", "toileting_hygiene", 3);
    pick("transferring", "transfer", 5);
    update("assessment", { gg_functional: { ...gg, ...next } });
  };

  return (
    <SectionWrap eyebrow="Step 7 · GG Functional" title="GG-style Functional Scores (0 = dependent · max = independent)"
      testid="nsa-section-gg"
      description="CMS GG-style self-care & mobility snapshot. Alora-compatible. Use 'Auto-suggest' to pre-fill from Step 2 PCST codes — then edit.">
      <div className="mb-3">
        <button type="button" onClick={autoSuggest}
          className="h-9 px-4 rounded-md border border-[#b6c9dc] bg-[#eaf1f7] text-[#0f3b5f] text-[12.5px] font-medium hover:bg-[#dce9f3]"
          data-testid="nsa-gg-auto">
          Auto-suggest from Step 2 PCST codes
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {GG_ITEMS.map((i) => (
          <SelectInput key={i.key}
            label={`${i.label} (0–${i.max})`}
            value={gg[i.key]}
            onChange={(v) => update("assessment", { gg_functional: { ...gg, [i.key]: v } })}
            options={[...Array(i.max + 1).keys()].map((n) => ({ value: String(n), label: String(n) }))}
            testid={`nsa-gg-${i.key.replace(/_/g, "-")}`} />
        ))}
        <SelectInput label="Telephone — N/A option"
          value={gg.telephone === "NA" ? "NA" : ""}
          onChange={(v) => update("assessment", { gg_functional: { ...gg, telephone: v || "" } })}
          options={NA_OPT}
          testid="nsa-gg-telephone-na" />
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Prior functioning + multi-factor fall
// ---------------------------------------------------------------------------
export function PriorFunctioningSection() {
  const { data, update } = useWizard();
  const p = data.assessment?.prior_functioning || {};
  const set = (patch) => update("assessment", { prior_functioning: { ...p, ...patch } });
  return (
    <SectionWrap eyebrow="Step 7 · Prior Functioning" title="Prior Functioning (before current episode)"
      testid="nsa-section-prior">
      <Row>
        <SelectInput label="Self-care" value={p.self_care}
          onChange={(v) => set({ self_care: v })} options={INDEP_LEVELS} testid="nsa-prior-self-care" />
        <SelectInput label="Ambulation" value={p.ambulation}
          onChange={(v) => set({ ambulation: v })} options={INDEP_LEVELS} testid="nsa-prior-ambulation" />
        <SelectInput label="Transfer" value={p.transfer}
          onChange={(v) => set({ transfer: v })} options={INDEP_LEVELS} testid="nsa-prior-transfer" />
        <SelectInput label="Household tasks" value={p.household_tasks}
          onChange={(v) => set({ household_tasks: v })} options={INDEP_LEVELS} testid="nsa-prior-household" />
        <SelectInput label="Multi-factor falls assessment done?" value={p.multi_factor_fall_done}
          onChange={(v) => set({ multi_factor_fall_done: v })} options={YN} testid="nsa-prior-mfra-done" />
        {p.multi_factor_fall_done === "Yes" && (
          <SelectInput label="— Indicated fall risk?" value={p.multi_factor_fall_risk}
            onChange={(v) => set({ multi_factor_fall_risk: v })} options={YN} testid="nsa-prior-mfra-risk" />
        )}
      </Row>
    </SectionWrap>
  );
}


// =========================================================================
// WAVE 2 — additional NSA sections (sensory, lifestyle, weekly behaviors,
// continence detail, IADL, transport, housekeeping, activities, meds-detail,
// general notes, patient signature).
// =========================================================================

// ---------------------------------------------------------------------------
// SECTION: Sensory — Nose / Pharynx / Mouth
// ---------------------------------------------------------------------------
export function SensorySection() {
  const { data, update } = useWizard();
  const s = data.assessment?.sensory || {};
  const set = (patch) => update("assessment", { sensory: { ...s, ...patch } });
  const denyAll = () => set({
    nasal_congestion: "No", loss_of_smell: "No",
    pharynx_hoarseness: "No", pharynx_sore_throat: "No",
  });
  return (
    <SectionWrap eyebrow="Step 7 · ENT & Mouth" title="Nose · Pharynx · Mouth"
      testid="nsa-section-sensory"
      description="Vision and hearing are captured in Step 4. This covers ENT and oral findings.">
      <div className="mb-3">
        <DeniesAllButton onClick={denyAll} testid="nsa-sensory-deny" />
      </div>
      <Row>
        <SelectInput label="Nasal congestion / sinus problem" value={s.nasal_congestion}
          onChange={(v) => set({ nasal_congestion: v })} options={YN} testid="nsa-sensory-nasal" />
        <SelectInput label="Loss of smell" value={s.loss_of_smell}
          onChange={(v) => set({ loss_of_smell: v })} options={YN} testid="nsa-sensory-smell" />
        <SelectInput label="Pharyngeal hoarseness" value={s.pharynx_hoarseness}
          onChange={(v) => set({ pharynx_hoarseness: v })} options={YN} testid="nsa-sensory-hoarse" />
        <SelectInput label="Sore throat" value={s.pharynx_sore_throat}
          onChange={(v) => set({ pharynx_sore_throat: v })} options={YN} testid="nsa-sensory-sore" />
        <SelectInput label="Oral mucosa / mouth" value={s.mouth_status}
          onChange={(v) => set({ mouth_status: v })}
          options={MOUTH_STATES} testid="nsa-sensory-mouth" />
        <SelectInput label="Dentures" value={s.dentures}
          onChange={(v) => set({ dentures: v })}
          options={DENTURES} testid="nsa-sensory-dentures" />
        <TextInput label="Oral / ENT notes" value={s.oral_notes}
          onChange={(v) => set({ oral_notes: v })} testid="nsa-sensory-notes" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Lifestyle — Smoking / Alcohol
// ---------------------------------------------------------------------------
export function LifestyleSection() {
  const { data, update } = useWizard();
  const l = data.assessment?.lifestyle || {};
  const set = (patch) => update("assessment", { lifestyle: { ...l, ...patch } });
  return (
    <SectionWrap eyebrow="Step 7 · Lifestyle" title="Smoking & Alcohol"
      testid="nsa-section-lifestyle">
      <Row>
        <SelectInput label="Smokes tobacco?" value={l.smokes}
          onChange={(v) => set({ smokes: v })} options={YN} testid="nsa-life-smokes" />
        {l.smokes === "Yes" && (
          <>
            <SelectInput label="Degree" value={l.smoking_degree}
              onChange={(v) => set({ smoking_degree: v })}
              options={DEGREES_LMH} testid="nsa-life-smoke-degree" />
            <TextInput label="Pack-years (if known)" value={l.smoking_pack_years}
              onChange={(v) => set({ smoking_pack_years: v })} testid="nsa-life-pack-years" />
          </>
        )}
        <SelectInput label="Alcohol use?" value={l.alcohol}
          onChange={(v) => set({ alcohol: v })} options={YN} testid="nsa-life-alcohol" />
        {l.alcohol === "Yes" && (
          <>
            <SelectInput label="Degree" value={l.alcohol_degree}
              onChange={(v) => set({ alcohol_degree: v })}
              options={ALCOHOL_DEGREES} testid="nsa-life-alcohol-degree" />
            <TextInput label="Drinks/week (if known)" value={l.alcohol_drinks_per_week}
              onChange={(v) => set({ alcohol_drinks_per_week: v })} testid="nsa-life-drinks-week" />
          </>
        )}
      </Row>
      {(l.smokes === "Yes" || l.alcohol === "Yes") && (
        <div className="mt-3">
          <TextInput label="Lifestyle notes" value={l.notes}
            onChange={(v) => set({ notes: v })} testid="nsa-life-notes" />
        </div>
      )}
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Weekly cognitive / behavioral symptoms
// ---------------------------------------------------------------------------
const WEEKLY_BEHAVIOR_ITEMS = [
  { key: "memory_deficit",           label: "Memory deficit" },
  { key: "impaired_decision_making", label: "Impaired decision-making" },
  { key: "verbal_disruption",        label: "Verbal disruption" },
  { key: "physical_aggression",      label: "Physical aggression" },
  { key: "disruptive_behavior",      label: "Disruptive behavior" },
  { key: "delusional_hallucinatory", label: "Delusional / hallucinatory / paranoid" },
];

export function WeeklyBehaviorsSection() {
  const { data, update } = useWizard();
  const b = data.assessment?.behaviors_weekly || {};
  const set = (patch) => update("assessment", { behaviors_weekly: { ...b, ...patch } });
  const markNone = () => {
    const next = { none: "Yes" };
    WEEKLY_BEHAVIOR_ITEMS.forEach((i) => (next[i.key] = "No"));
    set(next);
  };
  return (
    <SectionWrap eyebrow="Step 7 · Behaviors" title="Cognitive / Behavioral symptoms demonstrated ≥ 1×/week"
      testid="nsa-section-weekly-behaviors"
      description="Complements Step 3 behaviors (ADL interference). This is a weekly symptom-frequency snapshot.">
      <div className="mb-3">
        <DeniesAllButton onClick={markNone} label="None demonstrated weekly · mark all No" testid="nsa-wb-none" />
      </div>
      <Row>
        {WEEKLY_BEHAVIOR_ITEMS.map((i) => (
          <SelectInput key={i.key} label={i.label} value={b[i.key]}
            onChange={(v) => set({ [i.key]: v, none: "" })}
            options={YN} testid={`nsa-wb-${i.key.replace(/_/g, "-")}`} />
        ))}
      </Row>
      <div className="mt-3">
        <TextInput label="Behaviors notes" value={b.notes}
          onChange={(v) => set({ notes: v })} testid="nsa-wb-notes" />
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Continence Detail (frequency + briefs + toilet supports)
// ---------------------------------------------------------------------------
export function ContinenceDetailSection() {
  const { data, update } = useWizard();
  const cd = data.assessment?.continence_detail || {};
  const set = (patch) => update("assessment", { continence_detail: { ...cd, ...patch } });
  return (
    <SectionWrap eyebrow="Step 7 · Continence Detail" title="Continence Frequency & Supports"
      testid="nsa-section-continence-detail"
      description="Refines the continence baseline captured earlier.">
      <Row>
        <TextInput label="Bladder episodes per day" value={cd.bladder_freq_per_day}
          onChange={(v) => set({ bladder_freq_per_day: v })} testid="nsa-cd-bladder-freq" />
        <SelectInput label="Bladder: wears briefs?" value={cd.bladder_wears_briefs}
          onChange={(v) => set({ bladder_wears_briefs: v })} options={YN} testid="nsa-cd-bladder-briefs" />
        <TextInput label="Bowel episodes per day" value={cd.bowel_freq_per_day}
          onChange={(v) => set({ bowel_freq_per_day: v })} testid="nsa-cd-bowel-freq" />
        <SelectInput label="Bowel: wears briefs?" value={cd.bowel_wears_briefs}
          onChange={(v) => set({ bowel_wears_briefs: v })} options={YN} testid="nsa-cd-bowel-briefs" />
        <SelectInput label="Raised toilet seat needed?" value={cd.raised_toilet_seat}
          onChange={(v) => set({ raised_toilet_seat: v })} options={YN} testid="nsa-cd-raised-seat" />
        <SelectInput label="Assistance with buttons / zippers?" value={cd.assist_buttons_zippers}
          onChange={(v) => set({ assist_buttons_zippers: v })} options={YN} testid="nsa-cd-buttons" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: IADL detail (meal prep, feeding, shopping assist, favorites, mealtimes)
// ---------------------------------------------------------------------------
export function IadlDetailSection() {
  const { data, update } = useWizard();
  const i = data.assessment?.iadl_detail || {};
  const set = (patch) => update("assessment", { iadl_detail: { ...i, ...patch } });
  const setMeal = (patch) => set({ mealtimes: { ...(i.mealtimes || {}), ...patch } });
  return (
    <SectionWrap eyebrow="Step 7 · IADL Detail" title="Meals · Feeding · Shopping Assist"
      testid="nsa-section-iadl-detail">
      <Row>
        <SelectInput label="Meal preparation assist" value={i.meal_prep_assist}
          onChange={(v) => set({ meal_prep_assist: v })}
          options={PARTIAL_FULL} testid="nsa-iadl-meal-prep" />
        <SelectInput label="Feeding assist" value={i.feeding_assist}
          onChange={(v) => set({ feeding_assist: v })}
          options={PARTIAL_FULL} testid="nsa-iadl-feeding" />
        <SelectInput label="Shopping assist" value={i.shopping_assist}
          onChange={(v) => set({ shopping_assist: v })}
          options={PARTIAL_FULL} testid="nsa-iadl-shopping" />
        <SelectInput label="Cut up food?" value={i.cutting_food}
          onChange={(v) => set({ cutting_food: v })} options={YN} testid="nsa-iadl-cut" />
        <SelectInput label="Puree food?" value={i.pureeing_food}
          onChange={(v) => set({ pureeing_food: v })} options={YN} testid="nsa-iadl-puree" />
        <TextInput label="Favorite foods" value={i.favorite_foods}
          onChange={(v) => set({ favorite_foods: v })} testid="nsa-iadl-fav-foods" />
        <TextInput label="Dietary supplements" value={i.supplements}
          onChange={(v) => set({ supplements: v })} testid="nsa-iadl-supplements" />
      </Row>
      <div className="mt-4">
        <FieldLabel>Typical mealtimes</FieldLabel>
        <Row>
          <TextInput label="Breakfast" placeholder="e.g. 7:30 AM" value={i.mealtimes?.breakfast}
            onChange={(v) => setMeal({ breakfast: v })} testid="nsa-iadl-mt-breakfast" />
          <TextInput label="Lunch" value={i.mealtimes?.lunch}
            onChange={(v) => setMeal({ lunch: v })} testid="nsa-iadl-mt-lunch" />
          <TextInput label="Dinner" value={i.mealtimes?.dinner}
            onChange={(v) => setMeal({ dinner: v })} testid="nsa-iadl-mt-dinner" />
          <TextInput label="Snacks" value={i.mealtimes?.snacks}
            onChange={(v) => setMeal({ snacks: v })} testid="nsa-iadl-mt-snacks" />
        </Row>
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Transportation
// ---------------------------------------------------------------------------
const TRANSPORT_MODES = [
  "Private vehicle · independent",
  "Private vehicle · must be driven",
  "Private vehicle · must be accompanied",
  "Public transportation",
  "Physically / mentally unable to travel",
  "Requires ambulance / medical transport",
];
const SHOPPING_CAPABILITY = [
  "Able to shop for all needs",
  "Small items / quantity only",
  "With assistance",
  "Physically / mentally unable to shop",
  "No opportunity to shop",
  "Chooses not to shop",
];
const HOUSEKEEPING_CAPABILITY = [
  "Independent",
  "Needs help with heavy items",
  "Light tasks only",
  "Needs regular help / supervision",
  "Unable",
  "Chooses not to",
];

export function TransportationSection() {
  const { data, update } = useWizard();
  const t = data.assessment?.transportation || {};
  const set = (patch) => update("assessment", { transportation: { ...t, ...patch } });
  return (
    <SectionWrap eyebrow="Step 7 · Transport" title="Transportation & Driving"
      testid="nsa-section-transportation">
      <Row>
        <SelectInput label="Patient drives self?" value={t.patient_drives}
          onChange={(v) => set({ patient_drives: v })} options={YN} testid="nsa-trans-drives" />
        <SelectInput label="Caregiver drives patient's car?" value={t.caregiver_drives_patient_car}
          onChange={(v) => set({ caregiver_drives_patient_car: v })} options={YN} testid="nsa-trans-cg-drives" />
        <SelectInput label="Transport needed for appointments / errands?"
          value={t.transport_needed_for_appointments}
          onChange={(v) => set({ transport_needed_for_appointments: v })}
          options={YN} testid="nsa-trans-needed" />
        <SelectInput label="Means of transportation" value={t.transport_mode}
          onChange={(v) => set({ transport_mode: v })}
          options={TRANSPORT_MODES} testid="nsa-trans-mode" />
        <TextInput label="Notes" value={t.notes}
          onChange={(v) => set({ notes: v })} testid="nsa-trans-notes" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Shopping / Housekeeping + task checklist
// ---------------------------------------------------------------------------
const HOUSEKEEPING_TASKS = [
  "Change linens", "Clean bathroom", "Clean kitchen", "Dust",
  "Feed / care for pets", "Laundry", "Make bed", "Sweep",
  "Take out trash", "Water plants", "Vacuum", "Other",
];

export function HousekeepingSection() {
  const { data, update } = useWizard();
  const hk = data.assessment?.housekeeping || {};
  const selected = new Set(hk.tasks_needing_help || []);
  const set = (patch) => update("assessment", { housekeeping: { ...hk, ...patch } });
  const toggle = (task) => {
    const next = new Set(selected);
    if (next.has(task)) next.delete(task); else next.add(task);
    set({ tasks_needing_help: Array.from(next) });
  };
  return (
    <SectionWrap eyebrow="Step 7 · Household" title="Shopping & Housekeeping Capability"
      testid="nsa-section-housekeeping">
      <Row>
        <SelectInput label="Shopping capability" value={hk.shopping_capability}
          onChange={(v) => set({ shopping_capability: v })}
          options={SHOPPING_CAPABILITY} testid="nsa-hk-shopping" />
        <SelectInput label="Housekeeping capability" value={hk.housekeeping_capability}
          onChange={(v) => set({ housekeeping_capability: v })}
          options={HOUSEKEEPING_CAPABILITY} testid="nsa-hk-capability" />
      </Row>
      <div className="mt-4">
        <FieldLabel>Tasks the patient needs help with</FieldLabel>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
          {HOUSEKEEPING_TASKS.map((task) => {
            const on = selected.has(task);
            return (
              <label key={task}
                className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[13px] cursor-pointer transition ${
                  on ? "bg-[#E7F1FB] border-[#1FA0E6] text-[#0B1E3A]"
                     : "bg-white border-[#D6E3EF] text-[#3A5275] hover:border-[#B7CADA]"
                }`}>
                <input type="checkbox" checked={on} onChange={() => toggle(task)}
                  className="w-4 h-4 accent-[#0A3D79]"
                  data-testid={`nsa-hk-task-${task.toLowerCase().replace(/[\/\s]+/g, "-")}`} />
                <span>{task}</span>
              </label>
            );
          })}
        </div>
      </div>
      <div className="mt-3">
        <TextInput label="Housekeeping notes (e.g. 'Other: …')" value={hk.notes}
          onChange={(v) => set({ notes: v })} testid="nsa-hk-notes" />
      </div>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Activities (permitted + favorite)
// ---------------------------------------------------------------------------
const ACTIVITY_PERMITS = [
  "Bedrest", "Up as tolerated", "Exercises as prescribed",
  "Assistive devices required", "No restrictions",
];

export function ActivitiesSection() {
  const { data, update } = useWizard();
  const a = data.assessment?.activities || {};
  const set = (patch) => update("assessment", { activities: { ...a, ...patch } });
  const selected = new Set(a.permitted || []);
  const toggle = (item) => {
    const next = new Set(selected);
    if (next.has(item)) next.delete(item); else next.add(item);
    set({ permitted: Array.from(next) });
  };
  return (
    <SectionWrap eyebrow="Step 7 · Activities" title="Activities Permitted & Favorites"
      testid="nsa-section-activities">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4">
        {ACTIVITY_PERMITS.map((item) => {
          const on = selected.has(item);
          return (
            <label key={item}
              className={`flex items-center gap-2 px-3 py-2 rounded-md border text-[13px] cursor-pointer transition ${
                on ? "bg-[#E7F1FB] border-[#1FA0E6] text-[#0B1E3A]"
                   : "bg-white border-[#D6E3EF] text-[#3A5275] hover:border-[#B7CADA]"
              }`}>
              <input type="checkbox" checked={on} onChange={() => toggle(item)}
                className="w-4 h-4 accent-[#0A3D79]"
                data-testid={`nsa-act-permit-${item.toLowerCase().replace(/\s+/g, "-")}`} />
              <span>{item}</span>
            </label>
          );
        })}
      </div>
      <Row>
        <TextInput label="Favorite activities at home" value={a.favorite_home}
          onChange={(v) => set({ favorite_home: v })} testid="nsa-act-favorite-home" />
        <TextInput label="Routine activities away from home" value={a.favorite_away}
          onChange={(v) => set({ favorite_away: v })} testid="nsa-act-favorite-away" />
        <TextInput label="Restrictions / notes" value={a.restrictions_notes}
          onChange={(v) => set({ restrictions_notes: v })} testid="nsa-act-notes" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: Medication management detail (extends Step 3's medication_assistance)
// ---------------------------------------------------------------------------
export function MedicationManagementSection() {
  const { data, update } = useWizard();
  const m = data.nursing?.medication_assistance || {};
  const set = (patch) => update("nursing", { medication_assistance: { ...m, ...patch } });
  return (
    <SectionWrap eyebrow="Step 7 · Medication Management" title="Self-management detail"
      testid="nsa-section-med-mgmt"
      description="Refines Step 3 Element 27 with management-specific details.">
      <Row>
        <SelectInput label="Self-responsible?" value={m.self_responsible}
          onChange={(v) => set({ self_responsible: v })} options={YN} testid="nsa-med-self" />
        <SelectInput label="Needs reminders?" value={m.needs_reminders}
          onChange={(v) => set({ needs_reminders: v })} options={YN} testid="nsa-med-reminders" />
        {m.needs_reminders === "Yes" && (
          <TextInput label="Reminders per day" value={m.reminders_per_day}
            onChange={(v) => set({ reminders_per_day: v })} testid="nsa-med-reminders-per-day" />
        )}
        <SelectInput label="Pillbox in use?" value={m.pillbox_used}
          onChange={(v) => set({ pillbox_used: v })} options={YN} testid="nsa-med-pillbox" />
        {m.pillbox_used === "Yes" && (
          <>
            <TextInput label="Weeks prefilled" value={m.pillbox_weeks_prefilled}
              onChange={(v) => set({ pillbox_weeks_prefilled: v })} testid="nsa-med-pillbox-weeks" />
            <TextInput label="Who manages pillbox" value={m.pillbox_managed_by}
              onChange={(v) => set({ pillbox_managed_by: v })} testid="nsa-med-pillbox-who" />
          </>
        )}
        <SelectInput label="Physically / mentally unable?" value={m.physical_mental_unable}
          onChange={(v) => set({ physical_mental_unable: v })} options={YN} testid="nsa-med-unable" />
        <SelectInput label="Resistant to taking meds?" value={m.resistant_to_meds}
          onChange={(v) => set({ resistant_to_meds: v })} options={YN} testid="nsa-med-resistant" />
        <SelectInput label="Medication schedule sheet present?" value={m.schedule_sheet_present}
          onChange={(v) => set({ schedule_sheet_present: v })} options={YN} testid="nsa-med-sched-sheet" />
      </Row>
    </SectionWrap>
  );
}

// ---------------------------------------------------------------------------
// SECTION: General notes + patient signature
// ---------------------------------------------------------------------------
export function NotesAndSignatureSection() {
  const { data, update } = useWizard();
  const a = data.assessment || {};
  const sig = a.patient_signature || {};
  return (
    <SectionWrap eyebrow="Step 7 · Closing" title="General Notes & Patient / Rep Signature"
      testid="nsa-section-closing">
      <NarrativeField label="General notes" testid="nsa-general-notes"
        fieldKey="assessment.general_notes"
        fieldPurpose="Final RN observations, follow-up plan, anything not captured above."
        value={a.general_notes}
        onChange={(v) => update("assessment", { general_notes: v })} />
      <div className="mt-4">
        <FieldLabel>Patient / representative signature</FieldLabel>
        <Row>
          <TextInput label="Signer name" value={sig.name}
            onChange={(v) => update("assessment", { patient_signature: { ...sig, name: v } })}
            testid="nsa-sig-name" />
          <TextInput label="Date" type="date" value={sig.date}
            onChange={(v) => update("assessment", { patient_signature: { ...sig, date: v } })}
            testid="nsa-sig-date" />
          <SelectInput label="Relationship" value={sig.relationship_to_patient}
            onChange={(v) => update("assessment", { patient_signature: { ...sig, relationship_to_patient: v } })}
            options={SIG_RELATIONSHIP} testid="nsa-sig-relationship" />
        </Row>
      </div>
    </SectionWrap>
  );
}
